import { DatePipe } from '@angular/common';
import {
  Component,
  ComponentRef,
  computed,
  inject,
  inputBinding,
  resource,
  signal,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { ApiClient } from '@core/services/api-client';
import { Badge } from '@shared/components/badge/badge';
import { Button } from '@shared/components/button/button';
import { Modal } from '@shared/components/modal/modal';
import { Toast, ToastMessage } from '@shared/components/toast/toast';
import { Candidates } from '@shared/models/candidate';

@Component({
  selector: 'app-candidates-page',
  imports: [Button, RouterLink, Badge, DatePipe, Modal],
  templateUrl: './candidates-page.html',
})
export class CandidatesPage {
  private apiClient = inject(ApiClient);

  private toastRef: ComponentRef<Toast> | null = null;
  readonly toastContainer = viewChild.required('toastOutlet', {
    read: ViewContainerRef,
  });
  private toastMessage = signal<string>('');

  readonly deleteModal = viewChild.required('deleteModal', { read: Modal });
  private candidateToDelete = signal<string>('');

  readonly candidates = resource({
    loader: () => this.apiClient.getAllCandidates(),
  });

  readonly isCandidatesLoading = computed(
    () => this.candidates.status() === 'loading'
  );

  readonly candidatesData = computed(
    () => this.candidates.value() as Candidates[]
  );

  // Toast
  private showToast(type: ToastMessage['type'], message: string): void {
    if (!this.toastRef) {
      this.toastRef = this.toastContainer().createComponent(Toast, {
        bindings: [inputBinding('toastMessage', this.toastMessage)],
      });
    }
    this.toastMessage.set(message);
    this.toastRef.instance.show(type, message);
  }

  readonly searchQuery = signal<string>('');
  readonly filteredCandidates = computed(() => {
    const query = this.searchQuery().toLowerCase();
    const list = this.candidatesData();

    return list.filter(candidate => {
      const email = candidate.invitedEmail.toLowerCase();
      const title = candidate.title.toLowerCase();
      const status = candidate.status.toLowerCase();

      const scheduledAtFormats: string[] = candidate.scheduledAt
        ? [
            new Date(candidate.scheduledAt)
              .toLocaleString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              })
              .toLowerCase(),
            new Date(candidate.scheduledAt).toISOString().toLowerCase(),
          ]
        : [];

      const expiredAtFormats: string[] = candidate.expiredAt
        ? [
            new Date(candidate.expiredAt)
              .toLocaleString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              })
              .toLowerCase(),
            new Date(candidate.expiredAt).toISOString().toLowerCase(),
          ]
        : [];

      const completedAtFormats: string[] = candidate.completedAt
        ? [
            new Date(candidate.completedAt)
              .toLocaleString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              })
              .toLowerCase(),
            new Date(candidate.completedAt).toISOString().toLowerCase(),
          ]
        : [];

      return (
        email.includes(query) ||
        title.includes(query) ||
        status.includes(query) ||
        scheduledAtFormats.some(f => f.includes(query)) ||
        expiredAtFormats.some(f => f.includes(query)) ||
        completedAtFormats.some(f => f.includes(query))
      );
    });
  });

  async onDeleteCandidate(invitationId: string) {
    this.candidateToDelete.set(invitationId);
    this.deleteModal().open();
  }

  async confirmDelete() {
    try {
      const invitationId = this.candidateToDelete();
      if (!invitationId) return;

      await this.apiClient.deleteCandidate(invitationId);
      this.candidates.reload();
      this.showToast('success', 'Candidate deleted successfully');
      this.candidateToDelete.set('');
    } catch (error) {
      console.error('Failed to delete candidate:', error);
      this.showToast('error', 'Failed to delete candidate. Please try again.');
    }
  }

  canAnalyze(status: string): boolean {
    return status === 'completed';
  }

  analyzeButtonClass(status: string): string {
    return this.canAnalyze(status)
      ? 'cursor-pointer'
      : 'opacity-50 cursor-not-allowed';
  }

  canDelete(status: string): boolean {
    return status === 'expired';
  }

  deleteButtonClass(status: string): string {
    return this.canDelete(status)
      ? 'cursor-pointer'
      : 'opacity-50 cursor-not-allowed';
  }
}
